import React, {FC, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Text,
    Tooltip,
    VStack,
    useToast,
} from "@chakra-ui/react";
import {InactiveOrderReminderController} from "../../controllers/InactiveOrderReminderController";
import {InactiveDealershipItem} from "../../shared/entities/Order/InactiveOrderReminder";
import {InactiveOrderReminderDealershipSettings} from "../../shared/entities/Order/InactiveOrderReminder";
import {buildRecipientEmails, parseEmailList} from "../../shared/entities/Order/InactiveOrderReminderRecipients";

interface InactiveOrderReminderRecipientsModalProps {
    isOpen: boolean;
    onClose: () => void;
    dealerships: InactiveDealershipItem[];
    onSaved: () => void;
}

export const InactiveOrderReminderRecipientsModal: FC<InactiveOrderReminderRecipientsModalProps> = ({
    isOpen,
    onClose,
    dealerships,
    onSaved,
}) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const controller = new InactiveOrderReminderController(dispatch);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<InactiveOrderReminderDealershipSettings[]>([]);

    useEffect(() => {
        if (!isOpen || dealerships.length === 0) {
            return;
        }
        setLoading(true);
        controller
            .settings(dealerships.map((d) => d.dealershipId))
            .then((data) => {
                setRows(
                    (data || []).map((row) => ({
                        ...row,
                        dealershipEmailSettings:
                            row.dealershipEmailSettings?.length
                                ? row.dealershipEmailSettings
                                : parseEmailList(row.dealershipEmail).map((email) => ({
                                    email,
                                    sendEnabled: row.includeDealershipEmail,
                                })),
                    })),
                );
            })
            .finally(() => setLoading(false));
    }, [isOpen, dealerships]);

    const updateRow = (dealershipId: number, patch: Partial<InactiveOrderReminderDealershipSettings>) => {
        setRows((prev) => prev.map((r) => (r.dealershipId === dealershipId ? {...r, ...patch} : r)));
    };

    const toggleDealershipEmail = (dealershipId: number, email: string, sendEnabled: boolean) => {
        setRows((prev) =>
            prev.map((r) => {
                if (r.dealershipId !== dealershipId) {
                    return r;
                }
                const dealershipEmailSettings = (r.dealershipEmailSettings || []).map((e) =>
                    e.email === email ? {...e, sendEnabled} : e,
                );
                const includeDealershipEmail = dealershipEmailSettings.some((e) => e.sendEnabled);
                return {...r, dealershipEmailSettings, includeDealershipEmail};
            }),
        );
    };

    const toggleUser = (dealershipId: number, userId: number, sendEnabled: boolean) => {
        setRows((prev) =>
            prev.map((r) =>
                r.dealershipId === dealershipId
                    ? {
                        ...r,
                        users: r.users.map((u) => (u.id === userId ? {...u, sendEnabled} : u)),
                    }
                    : r,
            ),
        );
    };

    const handleSave = async () => {
        const withRecipients = rows.filter((r) => buildRecipientEmails(r).length > 0);
        if (withRecipients.length === 0) {
            toast({
                title: "Select at least one recipient email",
                status: "warning",
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        setSaving(true);
        const result = await controller.saveSettings({items: rows});
        setSaving(false);
        if (result?.None) {
            toast({title: "Failed to save settings", status: "error", duration: 4000, isClosable: true});
            return;
        }
        toast({title: "Email settings saved", status: "success", duration: 3000, isClosable: true});
        onSaved();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Email recipients settings</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    {loading ? (
                        <Flex justify="center" py={8}><Spinner/></Flex>
                    ) : (
                        <VStack align="stretch" spacing={4}>
                            {rows.map((row) => (
                                <Box key={row.dealershipId} borderWidth="1px" borderRadius="md" p={3}>
                                    <Text fontWeight="bold" mb={2} fontSize="13px">{row.dealershipName}</Text>
                                    {row.excluded && (
                                        <Text fontSize="12px" color="orange.500" mb={2}>
                                            Dealership is excluded from reminders list.
                                        </Text>
                                    )}
                                    {(() => {
                                        const dealershipEmailSettings =
                                            row.dealershipEmailSettings?.length
                                                ? row.dealershipEmailSettings
                                                : parseEmailList(row.dealershipEmail).map((email) => ({
                                                    email,
                                                    sendEnabled: row.includeDealershipEmail,
                                                }));
                                        return (
                                            <>
                                                <Text fontSize="13px" fontWeight="semibold" mb={1}>
                                                    Dealership emails
                                                </Text>
                                                {dealershipEmailSettings.length > 0 ? (
                                                    dealershipEmailSettings.map((entry) => (
                                                        <Flex key={entry.email} align="center" mb={1}>
                                                            <Checkbox
                                                                size="sm"
                                                                isChecked={entry.sendEnabled}
                                                                onChange={(e) =>
                                                                    toggleDealershipEmail(
                                                                        row.dealershipId,
                                                                        entry.email,
                                                                        e.target.checked,
                                                                    )
                                                                }
                                                                mr={2}
                                                            />
                                                            <Text fontSize="13px">{entry.email}</Text>
                                                        </Flex>
                                                    ))
                                                ) : (
                                                    <Text fontSize="13px" color="gray.500">—</Text>
                                                )}
                                            </>
                                        );
                                    })()}
                                    {row.users.length > 0 && (
                                        <>
                                            <Divider my={2}/>
                                            <Text fontSize="13px" fontWeight="semibold" mb={1}>Users</Text>
                                            {row.users.map((u) => (
                                                <Flex key={u.id} align="center" mb={1}>
                                                    <Checkbox
                                                        size="sm"
                                                        isChecked={u.sendEnabled}
                                                        onChange={(e) =>
                                                            toggleUser(row.dealershipId, u.id, e.target.checked)
                                                        }
                                                        mr={2}
                                                    />
                                                    <Tooltip label={u.login || "Unknown user"} placement="top">
                                                        <Text fontSize="13px" cursor="help">{u.email}</Text>
                                                    </Tooltip>
                                                </Flex>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} size="sm" onClick={onClose} isDisabled={loading || saving}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" size="sm" onClick={handleSave} isLoading={saving} isDisabled={loading}>
                        Save
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default InactiveOrderReminderRecipientsModal;
